import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BulkEntryGrid } from '../components/BulkEntryGrid';
import { useCompany } from '../contexts/CompanyContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { formatNumber, formatDate } from '../lib/utils';

const BulkGreyBills = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { activeCompany } = useCompany();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.get(`/grey-orders/${orderId}`);
        setOrder(res.data);
      } catch {
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const columns = useMemo(() => [
    {
      key: 'purchase_bill_number', label: 'Bill No.', required: true, width: '140px',
      placeholder: 'e.g. PB-001',
    },
    {
      key: 'party_name', label: 'Party Name', required: true, width: '180px',
      placeholder: 'Party / supplier name',
      defaultValue: order?.supplier_name || '',
    },
    {
      key: 'billed_quantity', label: 'Quantity', required: true, type: 'number', width: '120px',
      placeholder: '0',
      validate: (v) => {
        const num = parseFloat(v);
        if (isNaN(num) || num <= 0) return 'Quantity must be > 0';
        return null;
      },
    },
    {
      key: 'billed_date', label: 'Bill Date', required: true, type: 'date', width: '140px',
      defaultValue: new Date().toISOString().split('T')[0],
    },
    {
      key: 'bill_rate', label: 'Bill Rate', type: 'number', width: '100px',
      placeholder: order?.rate?.toString() || '',
    },
    {
      key: 'amount', label: 'Amount', type: 'number', width: '110px',
      placeholder: '0',
    },
    {
      key: 'freight_charges', label: 'Freight', type: 'number', width: '100px',
      placeholder: '0',
    },
    {
      key: 'remarks', label: 'Remarks', width: '160px',
      placeholder: 'Optional',
    },
  ], [order]);

  const handleSave = async (rows) => {
    const processed = rows.map(r => ({
      ...r,
      billed_quantity: parseFloat(r.billed_quantity),
      bill_rate: r.bill_rate ? parseFloat(r.bill_rate) : null,
      amount: r.amount ? parseFloat(r.amount) : 0,
      freight_charges: r.freight_charges ? parseFloat(r.freight_charges) : 0,
    }));
    const res = await api.post('/bulk/grey-bills', { order_id: orderId, rows: processed });
    if (res.data.success_count > 0) {
      toast.success(`${res.data.success_count} bill(s) created successfully`);
    }
    if (res.data.error_count > 0) {
      toast.error(`${res.data.error_count} row(s) had errors`);
    }
    return res.data;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-600">Order not found</p>
        <Button variant="outline" onClick={() => navigate('/grey-orders')} className="mt-4">Back to Orders</Button>
      </div>
    );
  }

  const statusColors = {
    'Pending': 'bg-red-50 text-red-700 border-red-200',
    'Partially Billed': 'bg-amber-50 text-amber-700 border-amber-200',
    'Completed': 'bg-teal-50 text-teal-700 border-teal-200',
  };

  return (
    <div className="space-y-6" data-testid="bulk-grey-bills-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/grey-orders/${orderId}`)} data-testid="bulk-grey-bills-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Bulk Add Bills</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Add multiple bills for order <span className="font-mono font-semibold">{order.order_number}</span> · <span className="theme-text font-medium">{activeCompany?.name}</span>
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate(`/grey-orders/${orderId}`)} className="gap-1.5">
          <FileText className="h-4 w-4" /> View Order
        </Button>
      </div>

      {/* Order Context Card */}
      <Card className="border-l-4" style={{ borderLeftColor: 'var(--c-500)' }}>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Order</p>
              <p className="font-mono font-bold text-slate-900">{order.order_number}</p>
            </div>
            <div>
              <p className="text-slate-500">Supplier</p>
              <p className="font-medium text-slate-900">{order.supplier_name}</p>
            </div>
            <div>
              <p className="text-slate-500">Quality</p>
              <p className="font-medium text-slate-900">{order.quality_name}</p>
            </div>
            <div>
              <p className="text-slate-500">Ordered / Billed</p>
              <p className="font-medium text-slate-900">
                {formatNumber(order.ordered_quantity)} / {formatNumber(order.total_billed_quantity)} {order.order_unit}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Status</p>
              <Badge className={statusColors[order.order_status]}>{order.order_status}</Badge>
            </div>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Rate: ₹{formatNumber(order.rate)}/{order.order_unit} · Remaining to bill: <span className="font-semibold text-slate-700">{formatNumber(order.ordered_quantity - order.total_billed_quantity)} {order.order_unit}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <BulkEntryGrid
            columns={columns}
            onSave={handleSave}
            entityName="Grey Bill"
            testIdPrefix="bulk-gb"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkGreyBills;
